import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert, CircularProgress,
  Container, Stepper, Step, StepLabel, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { Send, NavigateNext, NavigateBefore, CheckCircle, KeyboardArrowDown } from '@mui/icons-material';
import { formsAPI, applicationsAPI, draftsAPI, captchaAPI } from '../../services/api';
import { MultiStepFormRenderer } from '../../components/FormRenderer';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const PHASE_IDENTITY = 0;   // name + email
const PHASE_OTP = 1;        // verify email
const PHASE_FORM = 2;       // multi-step sections
const PHASE_SUBMITTED = 3;  // done

export default function ScholarshipForm() {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const resumeToken = searchParams.get('resume');

  const [form, setForm] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Identity
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');

  // OTP
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Draft
  const [draftToken, setDraftToken] = useState('');
  const [verified, setVerified] = useState(false);

  // Form
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [history, setHistory] = useState([]);
  const [phase, setPhase] = useState(PHASE_IDENTITY);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [resumeLink, setResumeLink] = useState('');

  const autoSaveTimer = useRef(null);
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [parsedSettings, setParsedSettings] = useState({});
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Show a "more below" hint when the page extends past the viewport and the
  // user isn't near the bottom yet. Purely a visual cue (also scrolls on click).
  useEffect(() => {
    const update = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const nearBottom = window.scrollY >= scrollable - 80;
      setShowScrollHint(scrollable > 120 && !nearBottom);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const t = setInterval(update, 800); // catch late-loading content growth
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      clearInterval(t);
    };
  }, []);

  // Load form
  useEffect(() => {
    formsAPI.getPublic(formId)
      .then((r) => {
        const f = r.data.data;
        // Registration-type forms use the registration flow (scholar check,
        // self-fund, OTP). The acceptance email links here via /apply/<id>, so
        // hand off transparently to the registration page.
        if (f && f.form_type === 'registration') {
          // Preserve query params (especially ?resume=) when redirecting
          var qs = window.location.search || '';
          window.location.replace('/register/' + formId + qs);
          return;
        }
        setForm(f);
        const s = typeof f.schema === 'string' ? JSON.parse(f.schema) : f.schema;
        setSchema(s);
        var settings = typeof f.settings === 'string' ? JSON.parse(f.settings) : (f.settings || {});
        // Handle double-encoded JSON (stored as string inside JSONB)
        if (typeof settings === 'string') {
          try { settings = JSON.parse(settings); } catch(ep) { settings = {}; }
        }
        setCaptchaEnabled(!!settings.enable_captcha);
        if (settings.enable_captcha) {
          captchaAPI.generate().then((cr) => { setCaptchaQuestion(cr.data.data.question); setCaptchaToken(cr.data.data.token); });
        }
        setParsedSettings(settings);
        setLoading(false);
      })
      .catch((err) => { setError((err.response && err.response.data && err.response.data.error) || 'Form not found'); setLoading(false); });
  }, [formId]);

  // Resume from token
  useEffect(() => {
    if (!resumeToken || !formId) return;
    draftsAPI.loadDraft(formId, resumeToken)
      .then((r) => {
        const d = r.data.data;
        setEmail(d.email);
        setName(d.name);
        setDraftToken(resumeToken);
        setVerified(d.verified);
        if (d.responses) {
          const resp = typeof d.responses === 'string' ? JSON.parse(d.responses) : d.responses;
          setValues(resp || {});
        }
        if (d.verified) {
          setPhase(PHASE_FORM);
          setResumeLink(`${window.location.origin}/apply/${formId}?resume=${resumeToken}`);
        }
      })
      .catch((err) => { setError((err.response && err.response.data && err.response.data.error) || 'Could not load draft'); });
  }, [resumeToken, formId]);

  // Check email duplicates on blur
  const checkEmail = useCallback(async () => {
    if (!email || !formId) return;
    try {
      const res = await draftsAPI.checkEmail(formId, email);
      const data = res.data.data;
      if (data.duplicate) {
        setDuplicateWarning(data.message || 'An application with this email already exists.');
      } else {
        setDuplicateWarning('');
      }
    } catch(e) { /* ignore */ }
  }, [email, formId]);

  // Auto-save
  const autoSave = useCallback(async () => {
    if (!draftToken || !verified) return;
    if (Object.keys(values).length === 0 && !name) return;
    setSaving(true);
    try {
      // Filter out File objects before saving draft (they can't be serialized)
      var draftValues = {};
      Object.keys(values).forEach(function(k) {
        if (!(values[k] instanceof File)) { draftValues[k] = values[k]; }
      });
      await draftsAPI.saveDraft(formId, draftToken, draftValues, name);
      setLastSaved(new Date());
    } catch(e) { /* silent fail */ }
    setSaving(false);
  }, [draftToken, verified, values, name, formId]);

  // Trigger auto-save on value or name changes (debounced)
  useEffect(() => {
    if (!verified || !draftToken) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(autoSave, 3000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [values, name, autoSave, verified, draftToken]);

  const handleChange = (fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => ({ ...prev, [fieldId]: undefined }));
  };

  const handleFileChange = (fieldId, file) => {
    if (file) {
      setFiles((prev) => ({ ...prev, [fieldId]: file }));
      setValues((prev) => ({ ...prev, [fieldId]: file }));
    } else {
      // File removed
      setFiles((prev) => { var next = Object.assign({}, prev); delete next[fieldId]; return next; });
      setValues((prev) => ({ ...prev, [fieldId]: '' }));
    }
  };

  // Send OTP
  const handleSendOTP = async () => {
    if (!email) { setOtpError('Email is required'); return; }
    if (duplicateWarning) return;
    setOtpError('');
    try {
      await draftsAPI.sendOTP(formId, email, name);
      setOtpSent(true);
      setPhase(PHASE_OTP);
    } catch (err) {
      setOtpError((err.response && err.response.data && err.response.data.error) || 'Failed to send verification code');
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp) return;
    setOtpError('');
    try {
      const res = await draftsAPI.verifyOTP(formId, email, otp);
      const data = res.data.data;
      setDraftToken(data.draft_token);
      setVerified(true);

      // Load any previously saved draft responses
      try {
        const draftRes = await draftsAPI.loadDraft(formId, data.draft_token);
        const d = draftRes.data.data;
        if (d.name) setName(d.name);
        if (d.responses && typeof d.responses === 'object') {
          setValues(d.responses);
        }
        // Resume from last section if available
        if (d.last_section_index && d.last_section_index > 0) {
          setCurrentStep(d.last_section_index);
        }
      } catch(e5) {
        // No saved draft - fine, start fresh
      }

      setPhase(PHASE_FORM);
      const link = window.location.origin + '/apply/' + formId + '?resume=' + data.draft_token;
      setResumeLink(link);
    } catch (err) {
      setOtpError((err.response && err.response.data && err.response.data.error) || 'Verification failed');
    }
  };

  // Validate current step
  const validateStep = () => {
    setError('');
    if (currentStep === 0 && !name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (!schema) return true;
    const sections = (schema.sections || []).sort((a, b) => a.order - b.order);
    const section = sections[currentStep];
    if (!section) return true;

    const sectionFields = (schema.fields || []).filter((f) => f.section_id === section.id);
    const errs = {};
    const countWords = (t) => (!t || typeof t !== 'string') ? 0 : t.trim().split(/\s+/).filter((w) => w.length > 0).length;
    sectionFields.forEach((field) => {
      if (field.required && !values[field.id] && field.type !== 'file_upload' && field.type !== 'video') {
        errs[field.id] = 'This field is required';
      }
      if (field.required && field.type === 'file_upload' && !files[field.id] && !values[field.id]) {
        errs[field.id] = 'Please upload a file';
      }
      // Word-limit enforcement (min/max), when a value is present
      const v = field.validation || {};
      const val = values[field.id];
      if ((v.min_words || v.max_words) && typeof val === 'string' && val.trim()) {
        const wc = countWords(val);
        if (v.min_words && wc < v.min_words) {
          errs[field.id] = 'Please write at least ' + v.min_words + ' words (currently ' + wc + ').';
        } else if (v.max_words && wc > v.max_words) {
          errs[field.id] = 'Please keep this under ' + v.max_words + ' words (currently ' + wc + ').';
        }
      }
    });
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit final application
  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    // Filter out File objects from values before serialization (files are sent separately via FormData)
    var cleanValues = {};
    Object.keys(values).forEach(function(k) {
      if (!(values[k] instanceof File)) { cleanValues[k] = values[k]; }
    });
    // Final save
    if (draftToken) {
      try { await draftsAPI.saveDraft(formId, draftToken, cleanValues, name); } catch(e2) {}
    }

    try {
      const hasFiles = Object.keys(files).length > 0;
      if (hasFiles) {
        const formData = new FormData();
        formData.append('email', email);
        formData.append('name', name);
        formData.append('responses', JSON.stringify(cleanValues));
        Object.entries(files).forEach(([fieldId, file]) => { formData.append(fieldId, file); });
        await applicationsAPI.submit(formId, formData);
      } else {
        const submitData = { form_id: formId, email, name, responses: cleanValues };
        if (captchaEnabled) { submitData.captcha_token = captchaToken; submitData.captcha_answer = captchaAnswer; }
        await applicationsAPI.submit(formId, submitData);
      }
      setPhase(PHASE_SUBMITTED);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.error) || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  if (error && !form) return (
    <Container maxWidth="sm" sx={{ mt: 8 }}><Alert severity="error">{error}</Alert></Container>
  );

  if (phase === PHASE_SUBMITTED) return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            {form && form.form_type === 'general' ? 'Submission Complete!'
              : form && form.form_type === 'hiring' ? 'Application Received!' : 'Application Submitted!'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {form && form.form_type === 'general'
              ? 'Thank you, ' + name + '. Your response has been recorded. A confirmation will be sent to ' + email + '.'
              : form && form.form_type === 'hiring'
              ? 'Thank you for your application, ' + name + '. We will review it and reach out to you at ' + email + '.'
              : 'Thank you for applying, ' + name + '. You will receive a notification about your application status via email at ' + email + '.'}
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );

  const sections = schema ? (schema.sections || []).sort((a, b) => a.order - b.order) : [];
  var formSettings = parsedSettings;
  var bgStyle = formSettings.bg_image_url ? {
    backgroundImage: 'url(' + formSettings.bg_image_url + ')',
    backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
  } : {};
  var bgOpacityRaw = formSettings.bg_opacity;
  var bgOpacityPct = (bgOpacityRaw === undefined || bgOpacityRaw === null || bgOpacityRaw === '') ? 15 : Number(bgOpacityRaw);
  if (isNaN(bgOpacityPct)) bgOpacityPct = 15;
  const bgOverlay = formSettings.bg_image_url ? { position: 'relative', '&::before': {
    content: '""', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    bgcolor: 'background.default', opacity: (100 - bgOpacityPct) / 100, zIndex: 0,
  }} : {};
  const totalSteps = sections.length;

  // ---- Conditional section branching ----
  // Resolve where a section routes next: a section id, '__submit__', or
  // '__default__' (the next section in order). A branch-enabled radio field's
  // selected option overrides the section's next_section setting.
  const resolveNext = (section) => {
    if (!section) return '__submit__';
    const secFields = (schema && schema.fields ? schema.fields : []).filter((f) => f.section_id === section.id);
    const branchField = secFields.find((f) => f.type === 'radio' && f.branch_enabled && f.branch_map);
    if (branchField) {
      const chosen = values[branchField.id];
      if (chosen && branchField.branch_map[chosen]) return branchField.branch_map[chosen];
    }
    if (section.next_section) return section.next_section;
    return '__default__';
  };

  const currentSection = sections[currentStep];
  const nextDest = resolveNext(currentSection);
  const isSubmitStep = totalSteps === 0 || nextDest === '__submit__' ||
    (nextDest === '__default__' && currentStep === totalSteps - 1);

  const goNext = () => {
    if (!validateStep()) return;
    const dest = resolveNext(sections[currentStep]);
    if (dest === '__submit__') { handleSubmit(); return; }
    let nextIndex;
    if (dest === '__default__') {
      nextIndex = currentStep + 1;
    } else {
      nextIndex = sections.findIndex((s) => s.id === dest);
      if (nextIndex < 0) nextIndex = currentStep + 1;
    }
    if (nextIndex >= sections.length) { handleSubmit(); return; }
    setHistory((h) => h.concat([currentStep]));
    setCurrentStep(nextIndex);
    window.scrollTo(0, 0);
  };

  const goPrev = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const copy = h.slice();
      const prev = copy.pop();
      setCurrentStep(prev);
      return copy;
    });
    setFieldErrors({});
    window.scrollTo(0, 0);
  };

  // Sections shown in the progress bar = the path taken so far + current one.
  const pathIndices = history.concat([currentStep]);

  return (
    <Box sx={Object.assign({ minHeight: '100vh', bgcolor: '#f5f6fa', py: 4 }, bgStyle, bgOverlay)}>
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 4 }}>
            {formSettings.banner_url && <Box sx={{mb:2}}><img src={formSettings.banner_url} alt="" style={{width:'100%',maxHeight:200,objectFit:'cover',borderRadius:8}} /></Box>}
            <Typography variant="h4" color="primary" sx={{ mb: 1 }}>{form && form.title}</Typography>
            {phase === PHASE_IDENTITY && form && form.description && (
              /<[a-z][\s\S]*>/i.test(form.description)
                ? <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }} dangerouslySetInnerHTML={{__html: form.description}} />
                : <Typography variant="body1" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{form.description}</Typography>
            )}
            {form && form.deadline && (
              <Typography variant="body2" color="error">
                Deadline: {dayjs.utc(form.deadline).format('MMMM D, YYYY [at] h:mm A')} (UTC)
                {formSettings.deadline_tz_link ? (
                  <>
                    {' \u2014 '}
                    <Typography component="a" href={formSettings.deadline_tz_link} target="_blank" rel="noopener noreferrer"
                      variant="body2" sx={{ color: 'error.main', textDecoration: 'underline' }}>
                      find it in your local time
                    </Typography>
                  </>
                ) : null}
              </Typography>
            )}
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Phase: Identity */}
        {phase === PHASE_IDENTITY && (
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>Let's start with your email</Typography>
              <TextField fullWidth label="Email Address" type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setDuplicateWarning(''); }}
                onBlur={checkEmail}
                onKeyDown={(e) => { if (e.key === 'Enter' && email && !duplicateWarning) { e.preventDefault(); handleSendOTP(); } }}
                required
                error={!!duplicateWarning}
                sx={{ mb: duplicateWarning ? 0 : 3 }} />
              {duplicateWarning && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5, mb: 3, fontSize: 13 }}>
                  {duplicateWarning}
                </Typography>
              )}
              {otpError && <Alert severity="error" sx={{ mb: 2 }}>{otpError}</Alert>}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We'll send a verification code to your email to save your progress.
              </Typography>
              <Button variant="contained" size="large" onClick={handleSendOTP}
                disabled={!email || !!duplicateWarning}>
                Send Verification Code
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Phase: OTP */}
        {phase === PHASE_OTP && (
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Verify your email</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We sent a 6-digit code to <strong>{email}</strong>
              </Typography>
              <TextField label="Verification Code" value={otp} onChange={(e) => setOtp(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && otp.length === 6) { e.preventDefault(); handleVerifyOTP(); } }}
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                sx={{ mb: 2, maxWidth: 240, mx: 'auto' }} autoFocus />
              {otpError && <Alert severity="error" sx={{ mb: 2 }}>{otpError}</Alert>}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => setPhase(PHASE_IDENTITY)}>Back</Button>
                <Button variant="contained" onClick={handleVerifyOTP} disabled={otp.length < 6}>
                  Verify & Continue
                </Button>
              </Box>
              <Button size="small" sx={{ mt: 2 }} onClick={handleSendOTP}>Resend code</Button>
            </CardContent>
          </Card>
        )}

        {/* Phase: Multi-step form */}
        {phase === PHASE_FORM && (
          <>
            {/* Progress stepper — reflects the branching path taken so far */}
            {totalSteps > 1 && (
              <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Stepper activeStep={history.length} alternativeLabel>
                    {pathIndices.map((idx, i) => (
                      <Step key={i} completed={i < history.length}>
                        <StepLabel>{sections[idx] && sections[idx].title}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </CardContent>
              </Card>
            )}

            {/* Auto-save indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Logged in as {email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {saving ? 'Saving…' : lastSaved ? `Auto-saved ${dayjs(lastSaved).format('h:mm A')}` : ''}
              </Typography>
            </Box>

            {/* Resume link notice */}
            {resumeLink && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your progress is saved automatically. Copy and save this link (<Typography component="a" href={resumeLink} variant="body2" sx={{ fontWeight: 600 }}>here</Typography>) to resume later.
              </Alert>
            )}

            {/* Full name — collected after email verification, before the first section */}
            {currentStep === 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <TextField fullWidth label="Full Name" value={name} required autoFocus
                    onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setError(''); }}
                    error={!name.trim()}
                    helperText={!name.trim() ? 'Please enter your full name to continue' : ''} />
                </CardContent>
              </Card>
            )}

            {/* Current section */}
            <Card>
              <CardContent sx={{ p: 4 }}>
                {totalSteps > 0 && (
                  <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                    {(sections[currentStep] && sections[currentStep].title)}
                  </Typography>
                )}

                <MultiStepFormRenderer
                  schema={schema} values={values} onChange={handleChange}
                  onFileChange={handleFileChange} errors={fieldErrors}
                  scholarOnly={false} currentStep={currentStep}
                />

                {/* CAPTCHA - above buttons */}
                {captchaEnabled && isSubmitStep && (
                  <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>{captchaQuestion}</Typography>
                    <TextField size="small" label="Your answer" value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)} sx={{ width: 140 }} />
                  </Box>
                )}

                {/* Navigation */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button variant="outlined" startIcon={<NavigateBefore />}
                    onClick={goPrev}
                    disabled={history.length === 0}>
                    Previous
                  </Button>

                {!isSubmitStep ? (
                    <Button variant="contained" endIcon={<NavigateNext />}
                      onClick={goNext}>
                      Next
                    </Button>
                  ) : (
                    <Button variant="contained" endIcon={<Send />} onClick={handleSubmit} disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit Application'}
                    </Button>
                  )}
                </Box>

                {/* Step progress bar — approximate, based on the path taken */}
                {totalSteps > 1 && (
                  <LinearProgress variant="determinate"
                    value={isSubmitStep ? 100 : Math.min(100, (pathIndices.length / totalSteps) * 100)}
                    sx={{ mt: 3, borderRadius: 1, height: 6 }} />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Container>

      {/* Scroll-down hint — shows when there's content below the fold */}
      {showScrollHint && (
        <Box
          onClick={() => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' })}
          aria-hidden="true"
          sx={{
            position: 'fixed', right: 24, bottom: 24, zIndex: 1300,
            width: 44, height: 44, borderRadius: '50%',
            bgcolor: 'primary.main', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: 3, cursor: 'pointer', opacity: 0.85,
            animation: 'okcbounce 1.6s infinite',
            '@keyframes okcbounce': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(6px)' },
            },
            '&:hover': { opacity: 1 },
          }}
        >
          <KeyboardArrowDown />
        </Box>
      )}
    </Box>
  );
}
